import { useState, useEffect } from 'react';
import { Form, Row, Col, Drawer } from 'antd';
import { FiFilter } from '@react-icons/all-files/fi/FiFilter';
import { FiDownload } from '@react-icons/all-files/fi/FiDownload';

import { useDispatch, useSelector } from 'react-redux';
import { toQueryString, useSearchParams } from '../../../../utils';
import { downloadDealSubmissionsCsv } from '../../../../reducer/DealSubmission/actions';

import DealSubmissionAdvanceFilter from './Components/DealSubmissionAdvanceFilter';
import DealSubmissionAppliedFilter from './Components/DealSubmissionAppliedFilter';
import {
  DEFAULT_FILTERS,
  BEDS_OPTIONS,
  BATHS_OPTIONS,
  DEAL_TYPE_OPTIONS,
} from './constants';
import {
  ZorbaSelect,
  PlacesAutoComplete,
  ZorbaButton,
} from '../../../../components/forms';
import { BUTTON_VARIANT_DARK } from '../../../../components/forms/ZorbaButton/constants';

import './index.scss';

const DealSubmissionFilter = ({
  searchCriteria,
  setSearchCriteria,
  powerBuyers,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [isAdvanceSearch, setIsAdvanceSearch] = useState(false);
  const dispatch = useDispatch();
  const { isLoading } = useSelector(
    ({ DealSubmissionReducer }) => DealSubmissionReducer,
  );
  const [selectedAreas, setSelectedAreas] = useState([]);

  /**
   * Read filters from query params and set it as default values
   */
  useEffect(() => {
    const queryParamsToSet = {};
    searchParams.forEach((value, key) => {
      if (key in DEFAULT_FILTERS) {
        if (Array.isArray(searchCriteria[key])) {
          queryParamsToSet[key] = value.split(',');
        } else {
          queryParamsToSet[key] = value;
        }
      }
    });
    setSearchCriteria((oldValue) => ({
      ...oldValue,
      ...queryParamsToSet,
    }));
    form.setFieldsValue(queryParamsToSet);
  }, []);

  /**
   * Keep watching for changes on search criteria changes and set applied filters in current URL's query params
   */

  useEffect(() => {
    const { createdAtRange, areas, ...rest } = searchCriteria || {};

    const queryParams = toQueryString(rest) || {};
    const currentParamsString = window.location.search;

    if (currentParamsString !== queryParams.toString()) {
      setSearchParams(queryParams);
    }
  }, [searchCriteria]);

  const toggleAdvancedSearch = () => {
    setIsAdvanceSearch(!isAdvanceSearch);
  };

  const updateSearchCriteria = (newSearchCriteria) => {
    setSearchCriteria((oldValue) => ({
      ...oldValue,
      ...newSearchCriteria,
    }));
  };

  const onAdvanceFilterChange = (updatedSearchCriteria) => {
    updateSearchCriteria(updatedSearchCriteria);
    setIsAdvanceSearch(false); // Close drawer after applying filters
  };
  const onAdvanceSearchClear = (clearedSearchCriteria) => {
    updateSearchCriteria(clearedSearchCriteria);
    form.resetFields();
    setSelectedAreas([]);
    setIsAdvanceSearch(false); // Close drawer after clearing filters
  };

  const onRemovedFilter = (filterName) => {
    updateSearchCriteria({
      ...searchCriteria,
      [filterName]: DEFAULT_FILTERS[filterName],
    });

    if (filterName === 'areas') {
      setSelectedAreas([]);
    }
  };

  const onUserAutoCompleteHandler = (newSearchCriteria) => {
    setSearchCriteria((oldValue) => ({
      ...oldValue,
      sellerName: `${newSearchCriteria.firstName} ${newSearchCriteria.lastName}`,
    }));
  };

  const onPlacesChange = (address) => {
    if (!address?.formattedAddress) return;

    if (
      selectedAreas.filter(
        (x) => x.formattedAddress === address.formattedAddress,
      ).length > 0
    )
      return;

    setSelectedAreas([...selectedAreas, address].filter((x) => !!x));
    updateSearchCriteria({
      areas: [...selectedAreas, address].filter((x) => !!x),
    });
  };

  const onFieldChange = (fieldName, value) => {
    updateSearchCriteria({ [fieldName]: value });
  };

  const handleDownloadCsv = () => {
    dispatch(downloadDealSubmissionsCsv(searchCriteria));
  };

  return (
    <div className="filter-container">
      <Form layout="horizontal" name="querySearchForm" form={form}>
        <Row gutter={16} align="middle">
          {/* PlacesAutoComplete - 35% width */}
          <Col xs={24} sm={12} md={8} lg={8} xl={8}>
            <div className="form-field__item">
              <PlacesAutoComplete
                title=""
                clearInputOnSelect
                onChange={(address) => onPlacesChange(address)}
                fullAddressAndRegion
              />
            </div>
          </Col>

          {/* Beds select */}
          <Col xs={12} sm={6} md={4} lg={4} xl={4}>
            <div className="form-field__item">
              <ZorbaSelect
                placeholder="Min Beds"
                options={BEDS_OPTIONS}
                value={searchCriteria.minBedrooms}
                onChange={(value) => onFieldChange('minBedrooms', value)}
                allowClear
              />
            </div>
          </Col>

          {/* Baths select */}
          <Col xs={12} sm={6} md={4} lg={4} xl={4}>
            <div className="form-field__item">
              <ZorbaSelect
                placeholder="Min Baths"
                options={BATHS_OPTIONS}
                value={searchCriteria.minBathrooms}
                onChange={(value) => onFieldChange('minBathrooms', value)}
                allowClear
              />
            </div>
          </Col>

          {/* Deal type */}
          <Col xs={12} sm={6} md={4} lg={4} xl={4}>
            <div className="form-field__item">
              <ZorbaSelect
                placeholder="Deal type"
                options={DEAL_TYPE_OPTIONS}
                value={searchCriteria.source}
                onChange={(value) => onFieldChange('source', value)}
                allowClear
              />
            </div>
          </Col>

          {/* All filters button */}
          <Col xs={12} sm={6} md={4} lg={4} xl={4}>
            <div className="form-field__item">
              <ZorbaButton
                icon={<FiFilter />}
                variant={BUTTON_VARIANT_DARK}
                onClick={toggleAdvancedSearch}
                disabled={isLoading}
              >
                All Filters
              </ZorbaButton>
            </div>
          </Col>
        </Row>

        {/* Download CSV button - moved to separate row and aligned to the right */}
        <Row style={{ marginTop: '10px' }}>
          <Col
            xs={24}
            style={{ display: 'flex', justifyContent: 'flex-start' }}
          >
            <div className="form-field__item" style={{ width: '200px' }}>
              <ZorbaButton
                icon={<FiDownload />}
                variant={BUTTON_VARIANT_DARK}
                onClick={handleDownloadCsv}
                disabled={isLoading}
                fullWidth={false}
              >
                Download CSV
              </ZorbaButton>
            </div>
          </Col>
        </Row>
      </Form>

      <Drawer
        title="Advanced Filters"
        placement="right"
        onClose={() => setIsAdvanceSearch(false)}
        open={isAdvanceSearch}
        width={500}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <DealSubmissionAdvanceFilter
          onAdvanceSearchClear={onAdvanceSearchClear}
          onAdvanceFilterChanged={onAdvanceFilterChange}
          searchCriteria={searchCriteria}
          form={form}
          powerBuyers={powerBuyers}
          onUserAutoCompleteHandler={onUserAutoCompleteHandler}
          isLoading={isLoading}
          selectedAreas={selectedAreas}
          setSelectedAreas={setSelectedAreas}
        />
      </Drawer>

      <DealSubmissionAppliedFilter
        searchCriteria={searchCriteria}
        onRemovedFilter={onRemovedFilter}
        form={form}
        powerBuyers={powerBuyers}
      />
    </div>
  );
};

export default DealSubmissionFilter;
